import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useApi } from "../hooks/useApi";
import Sidebar from "../components/Sidebar";
import { Topbar, PageError, friendlyError } from "../components";

interface Tenant {
  id: string;
  slug: string;
  name: string;
  plan: string;
  max_orgs: number;
  features: string[];
  is_active: boolean;
  stripe_customer_id: string | null;
  notifications_enabled: boolean;
  created_at: string;
  updated_at: string;
}

interface Usage {
  active_users: number;
  total_users: number;
  active_connectors: number;
  storage_bytes: number;
  total_jobs: number;
  jobs_last_30d: number;
  failures_last_30d: number;
  last_backup_at: string | null;
}

interface User {
  id: string;
  email: string;
  role: string;
  is_active: boolean;
  is_platform_admin: boolean;
  last_login_at: string | null;
  created_at: string;
}

interface Connector {
  id: string;
  connector_type: string;
  display_name: string | null;
  is_active: boolean;
  created_at: string;
}

const PLAN_COLOURS: Record<string, string> = {
  free:       "var(--text3)",
  sme:        "#3B82F6",
  practice:   "var(--teal)",
  enterprise: "#8B5CF6",
};

export default function AdminTenantDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { get, patch } = useApi();

  const [tenant, setTenant]       = useState<Tenant | null>(null);
  const [usage, setUsage]         = useState<Usage | null>(null);
  const [users, setUsers]         = useState<User[]>([]);
  const [connectors, setConnectors] = useState<Connector[]>([]);
  const [loading, setLoading]     = useState(true);
  const [pageError, setPageError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMsg, setActionMsg] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    get<{ tenant: Tenant; usage: Usage; users: User[]; connectors: Connector[] }>(`/api/admin/tenants/${id}`)
      .then(r => {
        setTenant(r.tenant);
        setUsage(r.usage);
        setUsers(r.users);
        setConnectors(r.connectors);
      })
      .catch(err => setPageError(friendlyError(err)))
      .finally(() => setLoading(false));
  }, [get, id]);

  useEffect(() => { load(); }, [load]);

  const doAction = async (body: Record<string, any>, msg: string) => {
    setActionLoading(true);
    setActionMsg("");
    try {
      await patch(`/api/admin/tenants/${id}`, body);
      setActionMsg(msg);
      setTimeout(() => setActionMsg(""), 3000);
      load(); // refresh
    } catch (err: any) {
      setActionMsg(`Error: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="shell">
      <Sidebar />
      <div className="main-column">
        <Topbar
          title="Tenant Detail"
          subtitle={tenant ? `${tenant.name} (${tenant.slug})` : "Loading..."}
          action={
            <button className="btn btn-ghost btn-sm" onClick={() => navigate("/admin/tenants")}>
              Back to tenants
            </button>
          }
        />
        <main className="page">
          {pageError && <PageError message={pageError} onRetry={() => window.location.reload()} />}

          {!pageError && loading && (
            <div style={{ textAlign: "center", color: "var(--text3)", padding: "60px 0" }}>Loading...</div>
          )}

          {!pageError && !loading && tenant && usage && (
            <div style={{ maxWidth: 800 }}>

              {/* Tenant info + actions */}
              <div className="card" style={{ padding: 24, marginBottom: 24 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
                  <div>
                    <h3 style={{ margin: "0 0 4px", fontSize: 16, fontWeight: 600 }}>{tenant.name}</h3>
                    <span style={{ fontSize: 12, fontFamily: "var(--mono)", color: "var(--text3)" }}>{tenant.slug}</span>
                    <span style={{ margin: "0 8px", color: "var(--border2)" }}>|</span>
                    <span style={{ fontSize: 12, fontFamily: "var(--mono)", color: "var(--text3)" }}>
                      Created {new Date(tenant.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                    </span>
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <span className={`badge ${tenant.is_active ? "badge-success" : "badge-fail"}`}>
                      {tenant.is_active ? "Active" : "Suspended"}
                    </span>
                    <span style={{
                      fontSize: 12, fontFamily: "var(--mono)", fontWeight: 600,
                      padding: "3px 10px", borderRadius: "var(--r)",
                      background: "var(--surface2)",
                      color: PLAN_COLOURS[tenant.plan] ?? "var(--text2)",
                      textTransform: "uppercase",
                    }}>
                      {tenant.plan}
                    </span>
                  </div>
                </div>

                {/* Usage grid */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 20 }}>
                  <InfoItem label="Users" value={`${usage.active_users} / ${usage.total_users}`} />
                  <InfoItem label="Connectors" value={String(usage.active_connectors)} />
                  <InfoItem label="Storage" value={formatBytes(usage.storage_bytes)} />
                  <InfoItem label="Jobs (30d)" value={`${usage.jobs_last_30d} (${usage.failures_last_30d} failed)`} />
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 20 }}>
                  <InfoItem label="Total jobs" value={String(usage.total_jobs)} />
                  <InfoItem label="Last backup" value={usage.last_backup_at ? timeAgo(usage.last_backup_at) : "Never"} />
                  <InfoItem label="Max orgs" value={String(tenant.max_orgs)} />
                  <InfoItem label="Notifications" value={tenant.notifications_enabled ? "On" : "Off"} />
                </div>

                {/* Actions */}
                <div style={{ borderTop: "1px solid var(--border)", paddingTop: 16 }}>
                  <div style={{ fontSize: 11, fontFamily: "var(--mono)", color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 12 }}>
                    Actions
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                    {/* Plan change */}
                    <select
                      defaultValue={tenant.plan}
                      onChange={e => {
                        if (e.target.value !== tenant.plan) {
                          if (confirm(`Change plan from ${tenant.plan} to ${e.target.value}?`)) {
                            doAction({ plan: e.target.value }, `Plan changed to ${e.target.value}`);
                          } else {
                            e.target.value = tenant.plan;
                          }
                        }
                      }}
                      className="form-input"
                      style={{ width: 160, fontSize: 12 }}
                      disabled={actionLoading}
                    >
                      <option value="free">Free</option>
                      <option value="sme">SME</option>
                      <option value="practice">Practice</option>
                      <option value="enterprise">Enterprise</option>
                    </select>

                    {/* Suspend / Reactivate */}
                    {tenant.is_active ? (
                      <button
                        className="btn btn-ghost btn-sm"
                        style={{ color: "var(--red, #ef4444)", fontSize: 12 }}
                        disabled={actionLoading}
                        onClick={() => {
                          if (confirm(`Suspend ${tenant.name}? They will lose access immediately.`)) {
                            doAction({ is_active: false }, "Tenant suspended");
                          }
                        }}
                      >
                        Suspend tenant
                      </button>
                    ) : (
                      <button
                        className="btn btn-primary btn-sm"
                        style={{ fontSize: 12 }}
                        disabled={actionLoading}
                        onClick={() => doAction({ is_active: true }, "Tenant reactivated")}
                      >
                        Reactivate tenant
                      </button>
                    )}

                    {actionMsg && (
                      <span style={{
                        fontSize: 12, fontFamily: "var(--mono)", marginLeft: 8,
                        color: actionMsg.startsWith("Error") ? "var(--red, #ef4444)" : "var(--teal)",
                      }}>
                        {actionMsg}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Users */}
              <div className="card" style={{ overflow: "hidden", marginBottom: 24 }}>
                <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--border)" }}>
                  <h3 style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>Users ({users.length})</h3>
                </div>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Last login</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(u => (
                      <tr key={u.id}>
                        <td>
                          <span style={{ fontSize: 13 }}>{u.email}</span>
                          {u.is_platform_admin && (
                            <span style={{
                              fontSize: 10, fontFamily: "var(--mono)", background: "rgba(139,92,246,0.12)",
                              color: "#8B5CF6", padding: "1px 6px", borderRadius: "var(--r)", marginLeft: 6,
                            }}>
                              PLATFORM ADMIN
                            </span>
                          )}
                        </td>
                        <td className="td-mono" style={{ fontSize: 12 }}>{u.role}</td>
                        <td>
                          <span className={`badge ${u.is_active ? "badge-success" : "badge-fail"}`} style={{ fontSize: 10 }}>
                            {u.is_active ? "active" : "inactive"}
                          </span>
                        </td>
                        <td className="td-mono" style={{ fontSize: 12 }}>
                          {u.last_login_at ? timeAgo(u.last_login_at) : "Never"}
                        </td>
                        <td className="td-mono" style={{ fontSize: 12 }}>
                          {new Date(u.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Connectors */}
              <div className="card" style={{ overflow: "hidden" }}>
                <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--border)" }}>
                  <h3 style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>Connectors ({connectors.length})</h3>
                </div>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Name</th>
                      <th>Status</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {connectors.length === 0 && (
                      <tr><td colSpan={4} className="td-empty">No connectors</td></tr>
                    )}
                    {connectors.map(c => (
                      <tr key={c.id}>
                        <td>
                          <span style={{
                            fontSize: 11, fontFamily: "var(--mono)", fontWeight: 600,
                            textTransform: "uppercase",
                            color: c.connector_type === "freeagent" ? "#0DA690" : c.connector_type === "xero" ? "#13B5EA" : "var(--text2)",
                          }}>
                            {c.connector_type}
                          </span>
                        </td>
                        <td style={{ fontSize: 13 }}>{c.display_name || "-"}</td>
                        <td>
                          <span className={`badge ${c.is_active ? "badge-success" : "badge-neutral"}`} style={{ fontSize: 10 }}>
                            {c.is_active ? "connected" : "inactive"}
                          </span>
                        </td>
                        <td className="td-mono" style={{ fontSize: 12 }}>
                          {new Date(c.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

            </div>
          )}
        </main>
      </div>
    </div>
  );
}

function InfoItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontFamily: "var(--mono)", color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 2 }}>
        {label}
      </div>
      <div style={{ fontSize: 14, fontWeight: 600, fontFamily: "var(--mono)", color: "var(--text)" }}>
        {value}
      </div>
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (!bytes || bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}
